import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { PastePreparationService } from '../paste-preparation/PastePreparationService';
import { MixingTicketDataModel, ActiveDocumentVersionVm, DocumentCode } from 'shared/models';
import { DocumentService, TenantContextService } from 'shared/core';
import { partLabel } from '../workplan-list/vessel-split.util';

@Component({
  selector: 'app-mixing-ticket-print',
  templateUrl: './mixing-ticket-print.component.html',
  styleUrls: ['./mixing-ticket-print.component.less'],
  standalone: false,
})
export class MixingTicketPrintComponent implements OnInit, OnDestroy {
  data: MixingTicketDataModel | null = null;
  isLoading: boolean = true;
  formulationCodeMixingId: number = 0;

  /**
   * Compliance master document version (FormatNo + VersionNo) for the mixing record.
   * Sourced from DocumentService — same pattern as MixingPrint and MachineStatement reports.
   * Falls back to undefined if the active version isn't configured; template hides the
   * format line in that case rather than printing stale hardcoded text.
   */
  ActiveDocumentVersion?: ActiveDocumentVersionVm;

  private route$: Subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private pastePreparationService: PastePreparationService,
    private documentService: DocumentService,
    private tenantContext: TenantContextService,
  ) {}

  ngOnInit(): void {
    this.route$ = this.route.params.subscribe((params: Params) => {
      this.formulationCodeMixingId = +params['id'];
      this.loadTicket();
    });
  }

  ngOnDestroy(): void {
    this.route$.unsubscribe();
  }

  loadTicket(): void {
    // Load ticket data + document version in parallel; auto-print only after BOTH resolve
    // so the printed header carries the correct FormatNo / VersionNo.
    forkJoin({
      ticket: this.pastePreparationService.getMixingTicket(this.formulationCodeMixingId),
      docVersion: this.documentService.getActiveVersionByCode(DocumentCode.PasteMixingTicket).pipe(
        catchError(() => of(undefined as unknown as ActiveDocumentVersionVm))
      ),
    }).subscribe({
      next: ({ ticket, docVersion }) => {
        this.data = ticket;
        this.ActiveDocumentVersion = docVersion ?? undefined;
        this.isLoading = false;
        this.printWhenHeaderReady();
      },
      error: () => {
        this.isLoading = false;
        this.printWhenHeaderReady();
      },
    });
  }

  /** A fresh print tab may still be loading the company details for the header. */
  private printWhenHeaderReady(): void {
    this.tenantContext.whenCompanyInfoReady().then(() => setTimeout(() => {
      window.print();
      window.onafterprint = () => window.close();
    }, 1));
  }

  get totalMaterials(): number {
    if (!this.data) return 0;
    return (this.data.BaseMaterials?.length ?? 0) + (this.data.Pigments?.length ?? 0);
  }

  get ticketDateFormatted(): string {
    if (!this.data?.TicketDate) return new Date().toLocaleDateString('en-GB');
    return new Date(this.data.TicketDate).toLocaleDateString('en-GB');
  }

  get mixingLabel(): string {
    return this.data ? partLabel(this.data.MixingName, this.data.SubBatchSequence, this.data.SubBatchCount) : '';
  }
}
